if (!customElements.get('sticky-cta-bar')) {
  class StickyCtaBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.targetSelector = this.dataset.target || '.main-product__buy-buttons';
      this.observer = null;
      this.isVisible = false;

      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.sticky-cta__submit');
      this.priceCurrentEl = this.querySelector('[data-price-current]');
      this.priceCompareEl = this.querySelector('[data-price-compare]');
      this.variantInput = this.querySelector('input[name="id"]');
      this.badgeEl = this.querySelector('[data-badge]');
      this.swatchEls = this.querySelectorAll('[data-swatch]');
      this.optionValueEls = this.querySelectorAll('[data-option-value]');

      this.handleSubmit = this.handleSubmit.bind(this);
      this.onExternalVariantChange = this.onExternalVariantChange.bind(this);
      this.handleMobileTriggerClick = this.handleMobileTriggerClick.bind(this);
      this.handleColorTriggerClick = this.handleColorTriggerClick.bind(this);
    }

    connectedCallback() {
      this.initObserver();
      this.attachEventListeners();
      this.subscribeToPubSub();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.footerObserver?.disconnect();
      this.detachEventListeners();
      this.unsubscribeFromPubSub();
    }

    initObserver() {
      let target = document.querySelector(this.targetSelector);
      if (!target || target.offsetHeight === 0) {
        target = document.querySelector('mobile-variant-sheet');
      }
      if (!target) {
        console.warn(`[sticky-cta-bar] Target "${this.targetSelector}" not found`);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hide();
            } else if (entry.boundingClientRect.top < 0) {
              this.show();
            } else {
              this.hide();
            }
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(target);

      const footer = document.querySelector('.shopify-section-group-footer-group, footer, [data-section-type="footer"]');
      if (footer) {
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.hide();
              }
            });
          },
          { threshold: 0 }
        );
        this.footerObserver.observe(footer);
      }
    }

    attachEventListeners() {
      this.form?.addEventListener('submit', this.handleSubmit);
      this.mobileTriggerSize = this.querySelector('[data-trigger-size]');
      this.mobileTriggerSize?.addEventListener('click', this.handleMobileTriggerClick);
      this.mobileTriggerColor = this.querySelector('[data-trigger-color]');
      this.mobileTriggerColor?.addEventListener('click', this.handleColorTriggerClick);
    }

    detachEventListeners() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.mobileTriggerSize?.removeEventListener('click', this.handleMobileTriggerClick);
      this.mobileTriggerColor?.removeEventListener('click', this.handleColorTriggerClick);
    }

    subscribeToPubSub() {
      if (!theme.pubsub) return;

      this.unsubscribeVariant = theme.pubsub.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this.onExternalVariantChange
      );
    }

    unsubscribeFromPubSub() {
      this.unsubscribeVariant?.();
    }

    show() {
      if (this.isVisible) return;
      this.isVisible = true;
      this.classList.add('is-visible');
    }

    hide() {
      if (!this.isVisible) return;
      this.isVisible = false;
      this.classList.remove('is-visible');
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.disabled) return;

      const payload = this.buildPayload();

      if (!payload) return;

      this.setLoading(true);
      this.openCartWithLoading();

      try {
        const data = await this.addToCart(payload);

        if (this.isErrorResponse(data)) {
          this.handleError(data);
        } else {
          this.handleSuccess(data, payload);
        }
      } catch (error) {
        this.handleFetchError(error);
      } finally {
        this.setLoading(false);
      }
    }

    openCartWithLoading() {
      const cart = document.querySelector('cart-drawer');
      if (cart) {
        cart.setLoading(true);
        cart.open();
      }
    }

    buildPayload() {
      const variantId = this.variantInput?.value;

      if (!variantId) return null;

      const payload = {
        items: [{ id: variantId, quantity: 1 }],
      };

      const cart = document.querySelector('cart-drawer');
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map(({ id }) => id).join(',');
        payload.sections_url = window.Shopify.routes.root;
      }

      return payload;
    }

    async addToCart(payload) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify(payload),
      });

      return await response.json();
    }

    isErrorResponse(data) {
      return !!data.status;
    }

    handleError(data) {
      const cart = document.querySelector('cart-drawer');
      if (cart) {
        cart.setLoading(false);
        cart.close();
      }

      theme.pubsub?.publish(theme.pubsub.events.CART_ERROR, {
        source: 'sticky-cta',
        errors: data.errors || data.description,
        message: data.message,
      });
    }

    handleSuccess(data, payload) {
      const cart = document.querySelector('cart-drawer');
      if (cart) {
        if (cart.renderContents) {
          cart.renderContents(data);
        }
        cart.setLoading(false);
      }

      theme.pubsub?.publish(theme.pubsub.events.CART_UPDATE, {
        source: 'sticky-cta',
        productVariantId: payload.items?.[0]?.id,
        cartData: data,
        skipFetch: true,
      });
    }

    handleFetchError(error) {
      const cart = document.querySelector('cart-drawer');
      if (cart) {
        cart.setLoading(false);
        cart.close();
      }
      theme.pubsub?.publish(theme.pubsub.events.CART_ERROR, { error });
    }

    handleMobileTriggerClick() {
      const sheet = document.querySelector('mobile-variant-sheet');
      sheet?.querySelector('[data-sheet-trigger]')?.click();
    }

    handleColorTriggerClick() {
      const sheet = document.querySelector('mobile-variant-sheet');
      sheet?.openPanel('color');
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading.toString());
    }

    updateVariantInput(variant) {
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
    }

    updatePrice(variant) {
      if (this.priceCurrentEl) {
        this.priceCurrentEl.textContent = this.formatMoney(variant.price);
      }

      if (this.priceCompareEl) {
        if (variant.compare_at_price > variant.price) {
          this.priceCompareEl.textContent = this.formatMoney(variant.compare_at_price);
          this.priceCompareEl.classList.remove('is-hidden');
        } else {
          this.priceCompareEl.classList.add('is-hidden');
        }
      }

      this.updateBadge(variant);
    }

    updateBadge(variant) {
      if (!this.badgeEl) return;
      if (variant && variant.compare_at_price > variant.price) {
        const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        this.badgeEl.textContent = `-${pct}%`;
        this.badgeEl.classList.remove('is-hidden');
      } else {
        this.badgeEl.classList.add('is-hidden');
      }
    }

    updateSubmitButton(variant) {
      if (!this.submitButton) return;

      const buttonText = this.submitButton.querySelector('.sticky-cta__submit-text');
      if (!buttonText) return;

      if (!variant) {
        this.submitButton.disabled = true;
        buttonText.textContent = theme.locales?.product?.unavailable || 'Unavailable';
        return;
      }

      if (variant.available) {
        this.submitButton.disabled = false;
        buttonText.textContent = theme.locales?.product?.addToCart || 'Add to cart';
      } else {
        this.submitButton.disabled = true;
        buttonText.textContent = theme.locales?.product?.soldOut || 'Sold out';
      }
    }

    updateOptionDisplays(selectedOptions) {
      this.swatchEls.forEach((swatch) => {
        const index = parseInt(swatch.dataset.optionIndex, 10);
        const value = selectedOptions[index];
        if (value == null) return;
        const colorKey = value.toLowerCase().replace(/\s+/g, '-');
        if (window.theme?.swatchColors?.[colorKey]) {
          swatch.style.background = window.theme.swatchColors[colorKey];
        } else {
          swatch.style.background = colorKey;
        }
      });

      this.optionValueEls.forEach((el) => {
        const index = parseInt(el.dataset.optionIndex, 10);
        const value = selectedOptions[index];
        if (value != null) el.textContent = value;
      });
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat);
      }

      const amount = (cents / 100).toFixed(2);
      return `${amount} ${theme.currencyCode || ''}`.trim();
    }

    onExternalVariantChange(data) {
      if (data.source === 'sticky-cta') return;

      if (data.variant) {
        this.updateVariantInput(data.variant);
        this.updatePrice(data.variant);
        this.updateSubmitButton(data.variant);
      }

      if (data.selectedOptions) {
        this.updateOptionDisplays(data.selectedOptions);
      }
    }
  }

  customElements.define('sticky-cta-bar', StickyCtaBar);
}
